import React, { CSSProperties } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { enqueueSnackbar } from 'notistack';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, IconButton, LbFormControlLabel, Link } from '@infoods/lifebase-ui';

import classes from './VideoRow.module.scss';
import { Video } from '../../../../../interface/video';
import {
  EditDeleteButtonGroup,
  GridTableCell,
  GridTableRow,
} from '../../../../../../shared/components/table-components';
import { copyTextToClipboard } from '../../../../../../shared/utils/apiUtils';
import { VideoPreview } from '../../../../../components/VideoPreview/VideoPreview';
import { useVideosManagementContext } from '../../hooks/useVideosManagement';

type VideoRowProps = {
  video: Video;
};

const VideoRow = ({ video }: VideoRowProps) => {
  const { deleteVideo, openEditVideoModal } = useVideosManagementContext();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({
      id: video.id,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 1 : 0,
    cursor: isDragging ? 'grabbing' : undefined,
    boxShadow: isDragging ? 'var(--shadow-main-1)' : undefined,
  };

  const handleCopyLink = () => {
    copyTextToClipboard(video.youtubevideo);
    enqueueSnackbar('Link copied', { variant: 'success', autoHideDuration: 2000 });
  };

  return (
    <GridTableRow
      rootRef={setNodeRef}
      variant="row"
      style={style}
      data-test-id="video-row"
    >
      <IconButton
        color="tertiary"
        size="small"
        className={classes.dragHandle}
        {...listeners}
        {...attributes}
      >
        <DragIndicatorIcon />
      </IconButton>
      <GridTableCell>{video.position + 1}</GridTableCell>
      <GridTableCell>
        <VideoPreview
          imgSrc={video.image}
          videoLink={video.youtubevideo}
          className={classes.videoPreview}
        />
      </GridTableCell>
      <GridTableCell>{video.title}</GridTableCell>
      <GridTableCell className={classes.linkBox}>
        <Link href={video.youtubevideo} target="_blank" className={classes.link}>
          {video.youtubevideo}
        </Link>
        <IconButton color="tertiary" size="medium" onClick={handleCopyLink}>
          <ContentCopyIcon />
        </IconButton>
      </GridTableCell>
      <GridTableCell>
        <LbFormControlLabel
          control={<Checkbox checked={video.isDisplayed} color="primary" />}
          disabled
          label="Display after 1st login"
        />
      </GridTableCell>
      <EditDeleteButtonGroup
        modalTitle={`Are you sure you want to delete "${video.title}" video?`}
        onEdit={() => openEditVideoModal(video)}
        onDelete={() => deleteVideo(video.id)}
      />
    </GridTableRow>
  );
};

export { VideoRow };
